import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { z } from 'zod';
import { PageContainer } from '@/components/shared/PageContainer';
import { useFeatures } from '@/hooks/useFeatures';
import { PLATFORM_FEATURES } from '@/lib/constants/features';
import { queryClient } from '@/lib/utils/queryClient';
import { useSessionStore } from '@/store/sessionStore';
import { dateRangeRenderFilter } from '../../../-components/dateRangeRenderFilter';
import { ExportDialog } from '../../../-components/ExportDialog';
import { ReportToolbar } from '../../../-components/ReportToolbar';
import { SupplierCountryChart } from '../../../-components/tracing-operations/SupplierCountryChart';
import { SupplierMapChart } from '../../../-components/tracing-operations/SupplierMapChart';
import { TracedPosOverTimeChart } from '../../../-components/tracing-operations/TracedPosOverTimeChart';
import { TracingDepthChart } from '../../../-components/tracing-operations/TracingDepthChart';
import { TracingKpiGrid } from '../../../-components/tracing-operations/TracingKpiGrid';
import { useExport } from '../../../-hooks/useExport';
import { useReportFilterOptions as useFilterOptions } from '../../../-hooks/useFilterOptions';
import { useReportFilterPreferences } from '../../../-hooks/useReportFilterPreferences';
import { useReportFilters } from '../../../-hooks/useReportFilters';
import {
  tracingOperationsQueryOptions,
  useTracingOperationsQuery,
} from '../../../-hooks/useTracingOperationsQuery';
import { defaultDateRange } from '../../../-utils/dateUtils';

const searchSchema = z.object({
  filter: z.string().optional(),
});

export const Route = createFileRoute(
  '/(client)/report/(pages)/(analytics)/tracing-operations/',
)({
  validateSearch: searchSchema,
  loaderDeps: ({ search }) => ({ filter: search.filter }),
  loader: ({ deps }) => {
    const companyId = useSessionStore.getState().user?.companyId ?? '';
    if (!companyId) return;
    const filter = deps.filter ?? JSON.stringify(defaultDateRange());
    return queryClient.ensureQueryData(
      tracingOperationsQueryOptions(companyId, filter),
    );
  },
  component: TracingOperationsPage,
});

function TracingOperationsPage() {
  const { isFeatureEnabled } = useFeatures();
  const [exportOpen, setExportOpen] = useState(false);

  const filterOptions = useFilterOptions();
  const {
    filters,
    setFilters,
    appliedFilter,
    parsedFilter,
    showDifference,
    filterDefs,
  } = useReportFilters({ ...filterOptions, dateRangeRenderFilter });

  const { saveFilter } = useReportFilterPreferences('rpt-tracing-operations');
  useEffect(() => {
    saveFilter(appliedFilter);
  }, [appliedFilter, saveFilter]);

  const { data, isLoading } = useTracingOperationsQuery(appliedFilter);
  const { handleExport, isExporting } = useExport();

  if (!isFeatureEnabled(PLATFORM_FEATURES.CASCADE_TRACING)) {
    return null;
  }

  return (
    <PageContainer className='flex flex-col overflow-hidden'>
      {/* Sticky toolbar */}
      <div className='shrink-0 border-b border-border/60 bg-background px-4 py-3 z-10'>
        <ReportToolbar
          filterDefs={filterDefs}
          filters={filters}
          onFiltersChange={setFilters}
          onExport={() => setExportOpen(true)}
          helpUrl='https://app.intercom.com/a/apps/10569078-introduction-to-the-tracing-operations-report'
        />
      </div>

      {/* Scrollable content */}
      <div className='flex-1 min-h-0 overflow-y-auto'>
        <div className='flex flex-col gap-6 p-4 pb-10'>
          <TracingKpiGrid
            data={data}
            showDelta={showDifference}
            loading={isLoading}
          />

          <TracedPosOverTimeChart
            poPerDate={data?.poPerDate}
            startDate={parsedFilter.startDate}
            endDate={parsedFilter.endDate}
            loading={isLoading}
          />

          <TracingDepthChart
            tracingDepth={data?.tracingDepth}
            loading={isLoading}
          />

          <div className='grid grid-cols-1 gap-6 lg:grid-cols-2'>
            <SupplierMapChart
              locations={data?.suppliersLocations?.byGeoLocations}
              loading={isLoading}
            />
            <SupplierCountryChart
              byCountries={data?.suppliersLocations?.byCountries}
              loading={isLoading}
            />
          </div>
        </div>
      </div>

      <ExportDialog
        open={exportOpen}
        onOpenChange={setExportOpen}
        onExport={dataset => handleExport(appliedFilter, dataset)}
        isExporting={isExporting}
        showDataset
        filters={filters}
        filterDefs={filterDefs}
      />
    </PageContainer>
  );
}
